/* @jsx h */
import { defineComponent, h } from "vue";
import type { MenuCreatorCategory, MenuCreatorProduct } from "../types";
import { ProductCard } from "./ProductCard";

export interface EditMenuComponentProps {
  categories: MenuCreatorCategory[];
  onAddCategory: () => void;
  onAddProduct: (categoryId: string) => void;
  onEditCategory: (category: MenuCreatorCategory) => void;
  onDeleteCategory: (categoryId: string) => void;
  onEditProduct: (product: MenuCreatorProduct) => void;
  onDeleteProduct: (categoryId: string, productId: string) => void;
  onEditNutrition: (product: MenuCreatorProduct) => void;
  onShowProductDetails: (product: MenuCreatorProduct) => void;
}

export const EditMenuComponent = defineComponent<EditMenuComponentProps>({
  name: "EditMenuComponent",
  props: [
    "categories",
    "onAddCategory",
    "onAddProduct",
    "onEditCategory",
    "onDeleteCategory",
    "onEditProduct",
    "onDeleteProduct",
    "onEditNutrition",
    "onShowProductDetails",
  ],
  setup(props) {
    const categories = () => props.categories ?? [];

    return () => (
      <>
        <div class="mt-10 bg-gray-50 border border-gray-200 rounded-3xl px-8 py-10 shadow-sm">
          <div class="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] items-center">
            <div class="space-y-5">
              <div class="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white px-3 py-1 text-xs tracking-widest uppercase">
                <span class="material-symbols-outlined text-sm">translate</span>
                Serve guests in 25+ languages
              </div>
              <h1
                style="line-height: 1.2;"
                class="text-4xl lg:text-5xl font-serif font-light text-gray-900"
              >
                Build multilingual menus that showcase flavour{" "}
                <span class="font-semibold">and</span> nutritional confidence.
              </h1>
              <p class="text-lg text-gray-600 font-light leading-relaxed max-w-2xl">
                Automatically translate dishes, surface allergens, and publish
                transparent nutritional data in one elegant workflow. Delight
                international guests, satisfy EU 1169/2011 labelling
                requirements, and keep your kitchen team in sync.
              </p>
              <div class="grid gap-3 md:grid-cols-2 text-sm text-gray-700">
                <div class="flex items-start gap-3">
                  <span class="material-symbols-outlined text-base text-gray-500 mt-[2px]">
                    verified
                  </span>
                  <span>
                    Instantly localise menus into French, German, Italian,
                    English, and more.
                  </span>
                </div>
                <div class="flex items-start gap-3">
                  <span class="material-symbols-outlined text-base text-gray-500 mt-[2px]">
                    restaurant_menu
                  </span>
                  <span>
                    Attach rich nutritional labels and allergen disclosures
                    guests can trust.
                  </span>
                </div>
                <div class="flex items-start gap-3">
                  <span class="material-symbols-outlined text-base text-gray-500 mt-[2px]">
                    trending_up
                  </span>
                  <span>
                    Upsell signature dishes with AI-generated pairings and
                    sensory copy.
                  </span>
                </div>
                <div class="flex items-start gap-3">
                  <span class="material-symbols-outlined text-base text-gray-500 mt-[2px]">
                    shield
                  </span>
                  <span>
                    Stay compliant with allergen &amp; calorie disclosure
                    regulations worldwide.
                  </span>
                </div>
              </div>
              <div class="flex flex-wrap gap-4 pt-2">
                <button
                  type="button"
                  onClick={props.onAddCategory}
                  class="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium shadow-lg flex items-center gap-2"
                >
                  <span class="material-symbols-outlined text-sm">
                    add_circle
                  </span>
                  Create my first menu
                </button>
                <button
                  type="button"
                  class="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:border-gray-400 transition-colors font-medium flex items-center gap-2"
                >
                  Watch 90s overview
                </button>
              </div>
            </div>
            <div class="hidden lg:flex justify-end">
              <div class="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 w-full max-w-sm">
                <div class="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-4">
                  Live preview
                </div>
                <div class="space-y-4 text-sm font-light text-gray-700">
                  <div class="flex items-center justify-between">
                    <span>Translations ready</span>
                    <span class="font-medium">French · German</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span>Nutrition coverage</span>
                    <span class="font-medium">100% of dishes</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span>Allergen alerts</span>
                    <span class="font-medium">12 flagged</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span>Menu formats</span>
                    <span class="font-medium">QR, Print, Web</span>
                  </div>
                </div>
                <div class="mt-6 text-xs text-gray-400 leading-relaxed">
                  Keep your brand consistent across every channel while
                  empowering guests to choose confidently.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="max-w-3xl mx-auto my-12">
          <div class="text-center mt-12">
            <div class="flex items-center justify-center mb-8">
              <div class="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent flex-1 max-w-32"></div>
              <span class="text-sm text-gray-500 px-8 font-light tracking-wider uppercase">
                or craft manually
              </span>
              <div class="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent flex-1 max-w-32"></div>
            </div>
            <button
              type="button"
              onClick={props.onAddCategory}
              class="bg-white border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg hover:border-gray-400 hover:shadow-md transition-all font-medium"
            >
              <span class="material-symbols-outlined mr-3 align-text-bottom">
                palette
              </span>
              Start from Scratch
            </button>
          </div>
        </div>

        <div class="space-y-12">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-3xl font-serif font-light text-gray-900 mb-2">
                Menu Architecture
              </h2>
              <p class="text-gray-600 font-light">
                Organize your culinary offerings into elegant categories
              </p>
            </div>
            <button
              type="button"
              onClick={props.onAddCategory}
              class="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium shadow-lg"
            >
              <span class="material-symbols-outlined text-sm mr-2 align-text-bottom">
                add
              </span>
              Add Category
            </button>
          </div>

          {categories().length === 0 ? (
            <div class="text-center py-24 bg-white rounded-2xl shadow-lg">
              <div class="text-8xl mb-8 text-gray-300">🍽️</div>
              <h3 class="text-2xl font-serif font-light text-gray-900 mb-4">
                No categories yet
              </h3>
              <p class="text-gray-600 font-light text-lg">
                Begin by creating your first menu category
              </p>
            </div>
          ) : (
            categories().map((category) => (
              <div
                key={category.id}
                class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <div class="bg-gradient-to-r from-gray-50 to-white px-8 py-6 border-b border-gray-100">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-4">
                      <div
                        class="w-6 h-6 rounded-full shadow-sm border border-white"
                        style={`background-color: ${category.color}`}
                      ></div>
                      <div>
                        <h3 class="text-2xl font-serif font-medium text-gray-900">
                          {category.name}
                        </h3>
                        <span class="text-sm text-gray-500 font-light">
                          {category.products.length} exquisite{" "}
                          {category.products.length === 1 ? "item" : "items"}
                        </span>
                      </div>
                    </div>
                    <div class="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => props.onAddProduct(category.id)}
                        class="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:border-gray-400 hover:shadow-sm transition-all text-sm font-medium"
                      >
                        <span class="material-symbols-outlined text-sm mr-2 align-text-bottom">
                          add
                        </span>
                        Add Item
                      </button>
                      <button
                        type="button"
                        onClick={() => props.onEditCategory(category)}
                        class="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit Category"
                      >
                        <span class="material-symbols-outlined text-lg">
                          edit
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => props.onDeleteCategory(category.id)}
                        class="p-2 text-red-400 hover:text-red-600 transition-colors"
                        title="Delete Category"
                      >
                        <span class="material-symbols-outlined text-lg">
                          delete
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="p-8">
                  {category.products.length === 0 ? (
                    <div class="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                      <div class="text-6xl mb-4 text-gray-300">🍴</div>
                      <p class="text-gray-500 font-light text-lg">
                        No items in this category yet
                      </p>
                      <button
                        type="button"
                        onClick={() => props.onAddProduct(category.id)}
                        class="mt-4 text-gray-600 hover:text-gray-900 font-medium"
                      >
                        Add your first item
                      </button>
                    </div>
                  ) : (
                    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                      {category.products.map((product) => (
                        <ProductCard
                          key={product.id}
                          product={product}
                          onEdit={props.onEditProduct}
                          onDelete={(productId) =>
                            props.onDeleteProduct(category.id, productId)
                          }
                          onEditNutrition={props.onEditNutrition}
                          onViewDetails={props.onShowProductDetails}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </>
    );
  },
});
